/**
 * AgregarPartidaPresupuestoModal
 * Modal para agregar partidas al presupuesto
 * - Permite seleccionar partidas del catálogo maestro por área
 * - Permite crear una nueva partida directamente en el maestro
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiPlus, FiSearch, FiCheck } from 'react-icons/fi';
import presupuestoHierarchyService from '../services/presupuestoHierarchyService';
import { toast } from 'react-toastify';
import '../styles/modals.css';
import './AgregarPartidaPresupuestoModal.css';

import Loading from './Loading';
const AgregarPartidaPresupuestoModal = ({
  isOpen,
  onClose,
  areaId,
  maestroAreaId,
  tipo = 'venta',
  onSuccess
}) => {
  const [mode, setMode] = useState('select'); // 'select' o 'create'
  const [maestroPartidas, setMaestroPartidas] = useState([]);
  const [filteredPartidas, setFilteredPartidas] = useState([]);
  const [selectedPartidas, setSelectedPartidas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    unidad: '',
    fecha_inicio: '',
    fecha_termino: ''
  });
  const [creating, setCreating] = useState(false);
  const [proyectoFechas, setProyectoFechas] = useState({
    fecha_inicio_planeado: null,
    fecha_termino_planeada: null
  });

  useEffect(() => {
    if (isOpen && areaId) {
      setMode('select');
      setSelectedPartidas([]);
      setSearchTerm('');
      setFormData({
        nombre: '',
        descripcion: '',
        unidad: '',
        fecha_inicio: '',
        fecha_termino: ''
      });
      loadMaestroPartidas();
      if (tipo === 'costo') {
        loadProyectoFechas();
      }
    }
  }, [isOpen, areaId, tipo]);

  useEffect(() => {
    // Filter partidas based on search term
    if (searchTerm.trim() === '') {
      setFilteredPartidas(maestroPartidas);
    } else {
      const filtered = maestroPartidas.filter(partida =>
        partida.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (partida.descripcion && partida.descripcion.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredPartidas(filtered);
    }
  }, [searchTerm, maestroPartidas]);

  const loadMaestroPartidas = async () => {
    setLoading(true);
    try {
      // Load all partidas from the master catalog (now independent, not grouped by area)
      const response = await presupuestoHierarchyService.getMaestroPartidas();
      const partidas = response.data.results || response.data;
      setMaestroPartidas(partidas);
      setFilteredPartidas(partidas);
    } catch (error) {
      console.error('Error loading maestro partidas:', error);
      toast.error('Error al cargar las partidas del catálogo');
    } finally {
      setLoading(false);
    }
  };

  const loadProyectoFechas = async () => {
    try {
      console.log('🔄 Cargando fechas del proyecto para área:', areaId);

      // Cargar el área para obtener el presupuesto de costo
      const areaResponse = await presupuestoHierarchyService.getCostoAreaHierarchy(areaId);
      const area = areaResponse.data;
      console.log('📦 Área cargada:', area);

      // El presupuesto_costo puede venir como ID o como objeto
      const presupuestoCostoId = area.presupuesto_costo_data?.id || area.presupuesto_costo;
      console.log('💰 Presupuesto de costo ID:', presupuestoCostoId);

      if (!presupuestoCostoId) {
        console.log('⚠️ No hay presupuesto de costo asociado');
        return;
      }

      // Cargar el presupuesto de costo completo
      const presupuestoResponse = await presupuestoHierarchyService.getPresupuestoCosto(presupuestoCostoId);
      const presupuestoCosto = presupuestoResponse.data;
      console.log('📊 Presupuesto de costo completo:', presupuestoCosto);

      // Obtener el ID del proyecto desde el presupuesto de costo
      const proyectoId = presupuestoCosto.proyecto_data?.id || presupuestoCosto.proyecto;
      console.log('🎯 Proyecto ID:', proyectoId);

      if (!proyectoId) {
        console.log('⚠️ No hay proyecto asociado al presupuesto');
        return;
      }

      // Cargar el proyecto completo con sus fechas
      const proyectoResponse = await presupuestoHierarchyService.getProyecto(proyectoId);
      const proyecto = proyectoResponse.data;
      console.log('📋 Proyecto completo:', proyecto);
      console.log('📅 Fecha inicio planeado:', proyecto.fecha_inicio_planeado);
      console.log('📅 Fecha término planeada:', proyecto.fecha_termino_planeada);

      setProyectoFechas({
        fecha_inicio_planeado: proyecto.fecha_inicio_planeado || null,
        fecha_termino_planeada: proyecto.fecha_termino_planeada || null
      });
      console.log('✅ Fechas del proyecto establecidas');
    } catch (error) {
      console.error('❌ Error loading proyecto fechas:', error);
      // No mostrar error al usuario, las fechas son opcionales
    }
  };

  const togglePartidaSelection = (partidaId) => {
    if (selectedPartidas.includes(partidaId)) {
      setSelectedPartidas(selectedPartidas.filter(id => id !== partidaId));
    } else {
      setSelectedPartidas([...selectedPartidas, partidaId]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssignPartidas = async () => {
    if (selectedPartidas.length === 0) {
      toast.warning('Selecciona al menos una partida');
      return;
    }

    setCreating(true);
    try {
      // Assign selected partidas to the area in presupuesto
      // For each selected partida, we need to create a relationship in presupuesto
      const requests = selectedPartidas.map(partidaId => {
        if (tipo === 'venta') {
          return presupuestoHierarchyService.createVentaPartida({
            area: areaId,
            partida_referencia: partidaId,
            nombre: maestroPartidas.find(p => p.id === partidaId)?.nombre,
            descripcion: maestroPartidas.find(p => p.id === partidaId)?.descripcion,
            unidad: maestroPartidas.find(p => p.id === partidaId)?.unidad
          });
        } else {
          return presupuestoHierarchyService.createCostoPartida({
            area: areaId,
            partida_referencia: partidaId,
            nombre: maestroPartidas.find(p => p.id === partidaId)?.nombre,
            descripcion: maestroPartidas.find(p => p.id === partidaId)?.descripcion,
            unidad: maestroPartidas.find(p => p.id === partidaId)?.unidad
          });
        }
      });

      await Promise.all(requests);

      toast.success(`${selectedPartidas.length} partida(s) asignada(s) exitosamente`);
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error assigning partidas:', error);
      toast.error(error.response?.data?.detail || 'Error al asignar las partidas');
    } finally {
      setCreating(false);
    }
  };

  const handleCreatePartida = async (e) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      toast.warning('El nombre de la partida es requerido');
      return;
    }

    setCreating(true);
    try {
      // Create new partida in the master catalog
      const newPartidaResponse = await presupuestoHierarchyService.createMaestroPartida({
        area: areaId,
        nombre: formData.nombre.trim(),
        descripcion: formData.descripcion.trim(),
        unidad: formData.unidad.trim() || '',
        status: 'activo'
      });

      const newPartidaId = newPartidaResponse.data.id;

      // Assign the new partida to the area in presupuesto
      if (tipo === 'venta') {
        await presupuestoHierarchyService.createVentaPartida({
          area: areaId,
          partida_referencia: newPartidaId,
          nombre: formData.nombre.trim(),
          descripcion: formData.descripcion.trim(),
          unidad: formData.unidad.trim() || ''
        });
      } else {
        await presupuestoHierarchyService.createCostoPartida({
          area: areaId,
          partida_referencia: newPartidaId,
          nombre: formData.nombre.trim(),
          descripcion: formData.descripcion.trim(),
          unidad: formData.unidad.trim() || '',
          fecha_inicio: formData.fecha_inicio || null,
          fecha_termino: formData.fecha_termino || null
        });
      }

      toast.success('Partida creada y asignada exitosamente');
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error creating partida:', error);
      toast.error(error.response?.data?.detail || 'Error al crear la partida');
    } finally {
      setCreating(false);
    }
  };

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-xl agregar-partida-presupuesto-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>Agregar Partida al Presupuesto</h2>
          <button className="close-btn" onClick={onClose}>
            <FiX />
          </button>
        </div>

        {/* Content */}
        <div className="modal-body">
          <div className="mode-tabs">
            <button
              className={`mode-tab ${mode === 'select' ? 'active' : ''}`}
              onClick={() => setMode('select')}
            >
              <FiSearch /> Seleccionar del Catálogo
            </button>
            <button
              className={`mode-tab ${mode === 'create' ? 'active' : ''}`}
              onClick={() => setMode('create')}
            >
              <FiPlus /> Crear Nueva Partida
            </button>
          </div>

          {mode === 'select' && (
            <div className="select-partidas-section">
              <div className="info-message">
                <p>Selecciona una o más partidas del catálogo maestro para agregar a esta área del presupuesto.</p>
              </div>

              {/* Search bar */}
              <div className="search-bar">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Buscar partidas por nombre o descripción..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Partidas list */}
              <div className="partidas-list">
                {loading ? (
                  <Loading fullScreen={false} size="sm" mensaje="Cargando partidas" />
                ) : filteredPartidas.length === 0 ? (
                  <div className="empty-state">
                    {searchTerm ? 'No se encontraron partidas con ese criterio' : 'No hay partidas en el catálogo maestro para esta área'}
                  </div>
                ) : (
                  filteredPartidas.map(partida => (
                    <div
                      key={partida.id}
                      className={`partida-item ${selectedPartidas.includes(partida.id) ? 'selected' : ''}`}
                      onClick={() => togglePartidaSelection(partida.id)}
                    >
                      <div className="partida-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedPartidas.includes(partida.id)}
                          onChange={() => {}}
                        />
                      </div>
                      <div className="partida-info">
                        <h4>{partida.nombre}</h4>
                        {partida.descripcion && <p>{partida.descripcion}</p>}
                        {partida.unidad && <span className="partida-unidad">Unidad: {partida.unidad}</span>}
                      </div>
                      {selectedPartidas.includes(partida.id) && (
                        <FiCheck className="check-icon" />
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Selected count */}
              {selectedPartidas.length > 0 && (
                <div className="selection-summary">
                  {selectedPartidas.length} partida(s) seleccionada(s)
                </div>
              )}
            </div>
          )}

          {mode === 'create' && (
            <form onSubmit={handleCreatePartida} className="partida-form">
              <div className="form-section">
                <div className="info-message">
                  <p>Crea una nueva partida en el catálogo maestro. Esta partida estará disponible para todos los presupuestos y se asignará automáticamente a esta área del presupuesto.</p>
                </div>

                <div className="form-group">
                  <label htmlFor="nombre">
                    Nombre de la Partida <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    placeholder="Ej: Diseño Gráfico"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="descripcion">Descripción</label>
                  <textarea
                    id="descripcion"
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleChange}
                    placeholder="Descripción opcional de la partida"
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="unidad">Unidad de Medida</label>
                  <input
                    type="text"
                    id="unidad"
                    name="unidad"
                    value={formData.unidad}
                    onChange={handleChange}
                    placeholder="Ej: horas, unidades, servicios"
                  />
                </div>

                {tipo === 'costo' && (
                  <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label htmlFor="fecha_inicio">
                        Fecha de Inicio
                        {proyectoFechas.fecha_inicio_planeado && proyectoFechas.fecha_termino_planeada && (
                          <span style={{ fontSize: '0.85em', color: '#6b7280', marginLeft: '0.5rem' }}>
                            (Rango: {proyectoFechas.fecha_inicio_planeado} - {proyectoFechas.fecha_termino_planeada})
                          </span>
                        )}
                      </label>
                      <input
                        type="date"
                        id="fecha_inicio"
                        name="fecha_inicio"
                        value={formData.fecha_inicio}
                        onChange={handleChange}
                        min={proyectoFechas.fecha_inicio_planeado || ''}
                        max={proyectoFechas.fecha_termino_planeada || ''}
                      />
                      {proyectoFechas.fecha_inicio_planeado && proyectoFechas.fecha_termino_planeada && (
                        <small style={{ color: '#6b7280', fontSize: '0.85em' }}>
                          📅 Debe estar entre {proyectoFechas.fecha_inicio_planeado} y {proyectoFechas.fecha_termino_planeada}
                        </small>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="fecha_termino">
                        Fecha de Término
                        {proyectoFechas.fecha_inicio_planeado && proyectoFechas.fecha_termino_planeada && (
                          <span style={{ fontSize: '0.85em', color: '#6b7280', marginLeft: '0.5rem' }}>
                            (Rango: {proyectoFechas.fecha_inicio_planeado} - {proyectoFechas.fecha_termino_planeada})
                          </span>
                        )}
                      </label>
                      <input
                        type="date"
                        id="fecha_termino"
                        name="fecha_termino"
                        value={formData.fecha_termino}
                        onChange={handleChange}
                        min={proyectoFechas.fecha_inicio_planeado || ''}
                        max={proyectoFechas.fecha_termino_planeada || ''}
                      />
                      {proyectoFechas.fecha_inicio_planeado && proyectoFechas.fecha_termino_planeada && (
                        <small style={{ color: '#6b7280', fontSize: '0.85em' }}>
                          📅 Debe estar entre {proyectoFechas.fecha_inicio_planeado} y {proyectoFechas.fecha_termino_planeada}
                        </small>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={creating}
          >
            Cancelar
          </button>
          {mode === 'select' ? (
            <button
              className="btn btn-primary"
              onClick={handleAssignPartidas}
              disabled={creating || selectedPartidas.length === 0}
            >
              {creating ? 'Asignando...' : (
                <>
                  <FiPlus /> Asignar Partidas ({selectedPartidas.length})
                </>
              )}
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={handleCreatePartida}
              disabled={creating || !formData.nombre.trim()}
            >
              {creating ? 'Creando...' : (
                <>
                  <FiPlus /> Crear y Asignar Partida
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.getElementById('modal-root'));
};

export default AgregarPartidaPresupuestoModal;
